/**
 * Ghost racing for one play session: captures every attempt (RunCapture),
 * keeps the personal-best recording in the library, and shows a ghost of
 * either the PB or the previous attempt.
 *
 * Ghost trajectories come from re-simulating the recording headless in short
 * slices between frames (so a long attempt never stalls the game), verified
 * against the recording's hashes. A PB ghost that does not verify on this
 * build is not shown: it would not be the run it claims to be.
 */
import { makeConfig } from '../core/Config';
import { createRuleSet } from '../movement/experiments';
import { GAME_RESPAWN_DELAY_TICKS, type Simulation } from '../simulation/Simulation';
import { formatTime } from '../ui/Hud';
import type { GhostSource } from '../ui/Settings';
import { GhostClock, GhostView } from './GhostView';
import type { RunRecording } from './RecordingFormat';
import type { ReplayLibrary } from './ReplayLibrary';
import { RunCapture, type CaptureInfo } from './RunCapture';
import { simulateTrajectory, type Trajectory } from './Trajectory';

export interface GhostRacerOptions {
  sim: Simulation;
  trackKey: string;
  title: string;
  /** The track's baseline record key (PB recordings are kept under it). */
  recordKey: string;
  library: ReplayLibrary | null;
  ineligible: CaptureInfo['ineligible'];
  addToScene(view: GhostView): void;
  setLabel(text: string | null): void;
  /** Something the player should know (a PB recording could not be saved, …). */
  notice(text: string): void;
}

interface Ghost {
  trajectory: Trajectory;
  label: string;
}

/** Ticks re-simulated per slice in game: about a millisecond or two of work each. */
const IN_GAME_SLICE = 240;

export class GhostRacer {
  private readonly capture: RunCapture;
  private readonly view: GhostView;
  private readonly clock = new GhostClock();
  private readonly abort = new AbortController();
  private readonly unsubscribe: (() => void)[] = [];
  private source: GhostSource = 'pb';
  private pb: Ghost | null = null;
  private last: Ghost | null = null;
  private latest: RunRecording | null = null;
  private pendingPb = false;
  /** Serialises background re-simulations (one at a time, newest wins). */
  private queue: Promise<void> = Promise.resolve();

  constructor(private readonly o: GhostRacerOptions) {
    const sim = o.sim;
    const ruleset = sim.recordKey() === o.recordKey ? 'xernous.baseline.phase3' : 'modified';
    this.capture = new RunCapture(sim, { trackId: sim.level.data.id, title: o.title, ruleset, ineligible: o.ineligible }, { onRecording: (r) => this.recorded(r) });
    const shape = sim.config.player;
    this.view = new GhostView(shape.radius, shape.height);
    o.addToScene(this.view);
    this.unsubscribe.push(
      sim.events.on('attempt-start', (e) => this.clock.attemptStarted(e.time)),
      sim.events.on('attempt-reset', () => {
        this.clock.attemptReset();
        // A PB mark never carries over to another attempt (e.g. a finish too long to record).
        this.pendingPb = false;
      }),
      sim.events.on('finish', (e) => this.clock.finished(e.seconds)),
    );
    void this.loadPb();
  }

  /** The most recent attempt that started its timer (for "watch" and "save"). */
  latestRecording(): RunRecording | null {
    return this.latest;
  }

  /** The live run just set an eligible personal best: keep its recording when it arrives. */
  markPersonalBest(): void {
    this.pendingPb = true;
  }

  apply(source: GhostSource, opacity: number, offset: number): void {
    this.source = source;
    this.view.setOpacity(opacity);
    this.clock.offset = offset;
    this.select();
  }

  afterTick(): void {
    this.capture.afterTick();
  }

  /** Per rendered frame: place the ghost at the live run's interpolated simulation time. */
  render(alpha: number): void {
    const sim = this.o.sim;
    this.view.update(this.active() ? this.clock.time(sim.state.tick - 1, alpha, sim.dt) : null);
  }

  /** Test hook: what the ghost shows now. */
  describe(): { source: GhostSource; label: string | null; visible: boolean; pbLoaded: boolean; lastLoaded: boolean } {
    return { source: this.source, label: this.active()?.label ?? null, visible: this.view.group.visible, pbLoaded: this.pb !== null, lastLoaded: this.last !== null };
  }

  dispose(): void {
    this.abort.abort();
    for (const u of this.unsubscribe) u();
    this.capture.dispose();
    this.view.dispose();
    this.o.setLabel(null);
  }

  /** Late results (storage, re-simulation) arriving after the session ended are dropped. */
  private notice(text: string): void {
    if (!this.abort.signal.aborted) this.o.notice(text);
  }

  private active(): Ghost | null {
    return this.source === 'pb' ? this.pb : this.source === 'last' ? this.last : null;
  }

  private select(): void {
    const g = this.active();
    this.view.setTrajectory(g?.trajectory ?? null);
    this.o.setLabel(g ? `Ghost · ${g.label}` : null);
  }

  private recorded(rec: RunRecording): void {
    this.latest = rec;
    const pb = rec.result.outcome === 'finished' && rec.result.ineligible.length === 0 && this.pendingPb;
    if (rec.result.outcome === 'finished') this.pendingPb = false;
    if (pb && this.o.library) {
      this.o.library.savePb(rec, this.o.trackKey).catch((err: unknown) => this.notice(`Your personal-best recording could not be kept: ${err instanceof Error ? err.message : String(err)}`));
    }
    const time = rec.result.seconds === null ? 'unfinished' : formatTime(rec.result.seconds);
    this.enqueue(rec, (t) => {
      this.last = { trajectory: t, label: `previous attempt (${time})` };
      if (pb) this.pb = { trajectory: t, label: `PB ${time}` };
    });
  }

  private async loadPb(): Promise<void> {
    const lib = this.o.library;
    if (!lib) return;
    try {
      const m = await lib.pbFor(this.o.recordKey);
      if (!m || this.abort.signal.aborted) return;
      const { recording } = await lib.load(m.id);
      this.enqueue(recording, (t) => {
        if (!this.pb) this.pb = { trajectory: t, label: `PB ${formatTime(recording.result.seconds ?? 0)}` };
      });
    } catch (err: unknown) {
      this.notice(`The personal-best ghost could not be loaded: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  private enqueue(rec: RunRecording, use: (t: Trajectory) => void): void {
    this.queue = this.queue.then(async () => {
      if (this.abort.signal.aborted) return;
      try {
        const sim = this.o.sim;
        const t = await simulateTrajectory(rec, { level: sim.level, config: makeConfig(), rules: createRuleSet(), respawnDelayTicks: GAME_RESPAWN_DELAY_TICKS }, { slice: IN_GAME_SLICE, signal: this.abort.signal });
        if (this.abort.signal.aborted) return;
        if (t.verification.status !== 'verified') {
          this.notice('A ghost recording does not reproduce on this build, so it is not shown.');
          return;
        }
        use(t);
        this.select();
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        this.notice(`A ghost could not be prepared: ${err instanceof Error ? err.message : String(err)}`);
      }
    });
  }
}
